import { Box, Chip, Stack, Tooltip, Typography, type ChipProps } from '@mui/material';
import { ATTEMPT_STATUS, DIFFICULTY, QUESTION_TYPES, QUIZ_KINDS, QUIZ_STATUS } from '../meta';
import type {
  CategoryRef,
  Difficulty,
  QuestionType,
  QuizKind,
  QuizStatus,
  StudentStatus,
} from '../types';

export function TypeChip({
  type,
  size = 'small',
}: {
  type: QuestionType;
  size?: ChipProps['size'];
}) {
  const meta = QUESTION_TYPES[type];
  const Icon = meta.icon;
  return (
    <Chip
      size={size}
      variant="outlined"
      icon={<Icon />}
      label={meta.short}
      sx={{ '& .MuiChip-icon': { fontSize: 16 } }}
    />
  );
}

export function DifficultyChip({ difficulty }: { difficulty: Difficulty | null }) {
  if (!difficulty) return null;
  const meta = DIFFICULTY[difficulty];
  return <Chip size="small" variant="soft" color={meta.color} label={meta.label} />;
}

export function QuizStatusChip({ status }: { status: QuizStatus }) {
  const meta = QUIZ_STATUS[status];
  return <Chip size="small" variant="soft" color={meta.color} label={meta.label} />;
}

export function AttemptStatusChip({ status }: { status: StudentStatus }) {
  const meta = ATTEMPT_STATUS[status];
  return <Chip size="small" variant="soft" color={meta.color} label={meta.label} />;
}

export function KindLabel({ kind }: { kind: QuizKind }) {
  return (
    <Typography variant="overline" color="text.secondary" component="span" sx={{ lineHeight: 1.4 }}>
      {QUIZ_KINDS[kind] ?? 'Simulado ENEM'}
    </Typography>
  );
}

/** Small colored dot + name for each category; full path on hover. */
export function CategoryChips({
  categories,
  max = 3,
}: {
  categories: CategoryRef[];
  max?: number;
}) {
  if (categories.length === 0) return null;
  const shown = categories.slice(0, max);
  return (
    <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: 'wrap' }}>
      {shown.map((c) => (
        <Tooltip key={c.id} title={c.path}>
          <Chip
            size="small"
            variant="soft"
            label={c.name}
            icon={<CategoryDot color={c.color} />}
            sx={{ maxWidth: 220 }}
          />
        </Tooltip>
      ))}
      {categories.length > max && (
        <Tooltip
          title={categories
            .slice(max)
            .map((c) => c.path)
            .join(' · ')}
        >
          <Chip size="small" variant="soft" label={`+${categories.length - max}`} />
        </Tooltip>
      )}
    </Stack>
  );
}

export function CategoryDot({ color, size = 8 }: { color: string | null; size?: number }) {
  return (
    <Box
      component="span"
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        flexShrink: 0,
        ml: '8px !important',
        bgcolor: color ?? 'text.disabled',
        display: 'inline-block',
      }}
    />
  );
}
